import { DynamicDrawUsage, InstancedBufferAttribute, InstancedMesh, Matrix4, Vector3 } from 'three';
import { luminance, type LinearRGB } from '../../core/color';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { octahedronSolid } from '../fabric/solids';
import type { WorldFeature } from '../types';
import { streetInstances } from './streetAsset';
import { HEARTH_PUFFS, hearthSmokePoseAt, planHearthSmoke } from './hearthSmoke';

export const hearthSmokeFeature: WorldFeature = {
  id: 'hearth-smoke',
  label: 'Hearth smoke',
  weight: 0.1,
  build(ctx) {
    const hearths = planHearthSmoke(ctx.roofs ?? [], ctx.rng.fork('hearths'));
    if (!hearths.length) return { objects: [], obstacles: [], animated: [], counts: {} };
    const builder = createMeshBuilder();
    const white = ctx.palette.highlightWhite.median.linear,
      dark = ctx.palette.lineworkDark.median.linear;
    const ash = luminance(white) * 0.92 + luminance(dark) * 0.08;
    const color: LinearRGB = [ash, ash, ash];
    octahedronSolid(
      builder,
      { color, surface: SURFACE.smoke, edgeId: 0, extent: [1, 1], strokeAngle: 0 },
      { origin: [0, 0, 0], yaw: 0 },
      [0, 0, 0],
      1
    );
    const geometry = builder.build().scale(1, 0.6, 1);
    const count = hearths.length * HEARTH_PUFFS;
    streetInstances(geometry, count, 1760);
    /* Smoke has coverage rather than a solid contour: the final pass leaves edge id zero uninked. */
    geometry.setAttribute(
      'aInstanceEdgeId',
      new InstancedBufferAttribute(new Float32Array(count), 1)
    );
    const opacity = new InstancedBufferAttribute(new Float32Array(count), 1).setUsage(
      DynamicDrawUsage
    );
    geometry.setAttribute('aSmokeOpacity', opacity);
    const mesh = new InstancedMesh(
      geometry,
      ctx.materials.painted({ instanced: true, hearthSmoke: true }),
      count
    );
    mesh.name = 'hearth-smoke';
    mesh.userData['hearths'] = hearths;
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    let activity = 1,
      target = 1,
      previous = 0;
    const matrix = new Matrix4(),
      scale = new Vector3();
    const animated = {
      id: 'hearth-smoke',
      setTimeOfDay(_minutes: number, value: number) {
        target = value > 0.05 ? 1 : 0;
      },
      update(time: number) {
        activity += (target - activity) * (1 - Math.exp(-Math.max(0, time - previous) * 1.5));
        previous = time;
        hearths.forEach((hearth, h) => {
          const visibility = activity + (1 - activity) * (h % 4 === 0 ? 0.65 : 0);
          for (let puff = 0; puff < HEARTH_PUFFS; puff++) {
            const i = h * HEARTH_PUFFS + puff;
            const pose = hearthSmokePoseAt(hearth, puff, time, visibility);
            matrix
              .makeRotationY(hearth.phase + pose.age * 0.4)
              .scale(scale.setScalar(pose.radius))
              .setPosition(...pose.position);
            mesh.setMatrixAt(i, matrix);
            opacity.setX(i, pose.opacity);
          }
        });
        opacity.needsUpdate = true;
        mesh.instanceMatrix.needsUpdate = true;
      },
    };
    animated.update(0);
    return {
      objects: [mesh],
      animated: [animated],
      obstacles: [],
      counts: { smokingHearths: hearths.length, smokeMeshes: 1 },
    };
  },
};
